import React, { useEffect, useState } from "react";
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButtons,
  IonMenuButton,
  IonLoading,
} from "@ionic/react";
import {
  Box,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  Typography,
  TablePagination,
  InputAdornment,
  Card,
  CardContent,
  Avatar,
  Button,
  Chip,
  IconButton,
  Grid,
  Stack,
  Divider,
} from "@mui/material";
import { 
  Search, 
  Person, 
  Refresh, 
  Phone, 
  AccountBalance,
  MoneyOff,
  TrendingUp,
} from "@mui/icons-material";
import { membersWithBalance } from "../../../api/customers/customers";

interface Debtor {
  member_id: string;
  phone: string;
  customer_name: string;
  total_balance_due: string;
}

const SundryDebtorsPage: React.FC = () => {
  const [debtors, setDebtors] = useState<Debtor[]>([]);
  const [status, setStatus] = useState({ loading: false, error: false });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [searchQuery, setSearchQuery] = useState("");

  const fetchDebtors = async () => {
    setStatus({ loading: true, error: false });
    try {
      const response = await membersWithBalance();
      console.log("Debtors response:", response);
      // Assuming response.data is the array of debtors
      setDebtors(response.data || []); 
    } catch (error) {
      console.error("Error fetching debtors:", error);
      setStatus({ loading: false, error: true });
    } finally {
      setStatus({ loading: false, error: false });
    }
  };

  useEffect(() => {
    fetchDebtors();
  }, []);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(event.target.value);
    setPage(0); // Reset to the first page on a new search
  };

  const handleRefresh = () => {
    fetchDebtors();
  };

  const filteredDebtors = debtors.filter((debtor) => {
    const customerName = (debtor.customer_name || "").toLowerCase().trim();
    const phone = (debtor.phone || "").toLowerCase();
    const query = searchQuery.toLowerCase();

    return (
      customerName.includes(query) ||
      phone.includes(query)
    );
  });

  // Calculate totals
  const totalDebt = filteredDebtors.reduce((sum, debtor) => {
    return sum + parseFloat(debtor.total_balance_due || "0");
  }, 0);

  const totalDebtors = filteredDebtors.length;

  const formatCurrency = (amount: string) => {
    return `₦${parseFloat(amount || "0").toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const getInitials = (name: string) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map(word => word.charAt(0))
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const getDebtSeverity = (amount: string) => {
    const value = parseFloat(amount || "0");
    if (value === 0) return "success";
    if (value < 10000) return "warning";
    return "error";
  };

  return (
    <IonPage className="pushDown">
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Sundry Debtors</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="ion-padding">
        <IonLoading
          isOpen={status.loading}
          message={"Fetching debtors..."}
          spinner="circles"
        />

        <Box mb={3}>
          <Typography variant="h4" fontWeight="700" gutterBottom color="primary">
            Sundry Debtors
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage and track customer outstanding balances
          </Typography>
        </Box>

        {/* Summary Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <AccountBalance />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {totalDebtors}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Debtors
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {formatCurrency(totalDebt.toString())}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Total Outstanding
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={4}>
            <Card 
              sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                color: 'white',
                borderRadius: 3
              }}
            >
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <MoneyOff />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="700">
                      {filteredDebtors.filter(d => parseFloat(d.total_balance_due) === 0).length}
                    </Typography>
                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                      Cleared Accounts
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
          <CardContent sx={{ p: 0 }}>
            <Box
              sx={{
                p: 3,
                pb: 2,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: 'wrap',
                gap: 2
              }}
            >
              <Box>
                <Typography variant="h6" fontWeight="600">
                  Debtors List
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {filteredDebtors.length} customers with outstanding balances
                </Typography>
              </Box>
              
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  placeholder="Search by name or phone..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search color="action" />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ 
                    width: { xs: '100%', sm: 300 },
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2
                    }
                  }}
                  size="small"
                />
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={handleRefresh}
                  disabled={status.loading}
                  sx={{ 
                    borderRadius: 2,
                    textTransform: 'none'
                  }}
                >
                  Refresh
                </Button>
              </Stack>
            </Box>

            <Divider />

            <Paper elevation={0}>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#f8fafc" }}>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Customer</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Phone</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }} align="right">Balance Due</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDebtors
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((debtor) => (
                      <TableRow
                        key={debtor.member_id}
                        sx={{ 
                          "&:hover": { 
                            backgroundColor: "#f8fafc",
                            transition: 'all 0.2s ease'
                          } 
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center">
                            <Avatar 
                              sx={{ 
                                bgcolor: '#e3f2fd', 
                                width: 40, 
                                height: 40, 
                                mr: 2,
                                fontWeight: 600,
                                color: '#1976d2'
                              }}
                            >
                              {getInitials(debtor.customer_name)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" fontWeight="500">
                                {debtor.customer_name?.trim() || "Unknown Customer"}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                ID: {debtor.member_id?.slice(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Box display="flex" alignItems="center">
                            <Phone sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                              {debtor.phone || "N/A"}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography 
                            variant="body1" 
                            fontWeight="600"
                            color={parseFloat(debtor.total_balance_due) > 0 ? 'error.main' : 'success.main'}
                          >
                            {formatCurrency(debtor.total_balance_due)}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Chip 
                            label={parseFloat(debtor.total_balance_due) === 0 ? "Cleared" : "Outstanding"}
                            color={getDebtSeverity(debtor.total_balance_due) as any}
                            size="small"
                            variant={parseFloat(debtor.total_balance_due) === 0 ? "filled" : "outlined"}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              
              {filteredDebtors.length === 0 && !status.loading && (
                <Box textAlign="center" py={6}>
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    No debtors found
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {searchQuery ? "Try adjusting your search criteria" : "No customers with outstanding balances"}
                  </Typography>
                </Box>
              )}
              
              <TablePagination
                component="div"
                count={filteredDebtors.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 100));
                  setPage(0);
                }}
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider'
                }}
              />
            </Paper>
          </CardContent>
        </Card>
      </IonContent>
    </IonPage>
  );
};

export default SundryDebtorsPage;